import type { Rule } from './types';

const VAGUE_PHRASES = [
  /\bmake sure to\b/i,
  /\bvalidate things properly\b/i,
  /\bas appropriate\b/i,
  /\bif needed\b/i,
  /\bif necessary\b/i,
  /\bproperly\b/i,
];

function wordCount(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}

function countCodeBlocks(body: string): { count: number; longCount: number } {
  const fence = /^```[\s\S]*?^```/gm;
  let count = 0;
  let longCount = 0;
  let m: RegExpExecArray | null;
  while ((m = fence.exec(body)) !== null) {
    count++;
    if (m[0].split('\n').length >= 15) longCount++;
  }
  return { count, longCount };
}

export const bodyRules: Rule[] = [
  {
    id: 'body.has-instructions-section',
    category: 'body',
    severity: 'warn',
    weight: 4,
    title: 'Has actionable instructions',
    appliesTo: 'both',
    run(ctx) {
      const body = ctx.parsed.body;
      const headingSynonyms = /^#{2,4}\s+(instructions|workflow|workflows|steps?|how\s*it\s*works|how to|usage|process|process flow|methodology|approach|overview|getting started|quick start|when to use|when to invoke|operations|tasks?|stages?|phases?|guide|guidelines?|rules?|playbook)/im;
      const stepCue = /^\s*(?:\d+\.|###\s+step|step\s*\d+\s*:|phase\s*\d+\s*:)/im;
      const directiveCue = /^\s*(?:always|never|first|then|next|finally|use|run|call|invoke|when |if )/im;
      const bulletList = /^\s*[-*]\s+/m;
      if (
        headingSynonyms.test(body) ||
        stepCue.test(body) ||
        directiveCue.test(body) ||
        (bulletList.test(body) && (body.match(/^\s*[-*]\s+/gm) ?? []).length >= 5)
      ) {
        return { status: 'pass', message: 'Actionable structure present.' };
      }
      return {
        status: 'warn',
        message: 'No instructions, steps, or directive language detected.',
        fix: 'Add `## Instructions` with numbered or `### Step N:` subsections, or use directive bullet points.',
      };
    },
  },
  {
    id: 'body.has-examples-section',
    category: 'body',
    severity: 'info',
    weight: 2,
    title: 'Has examples or sample input/output',
    appliesTo: 'both',
    run(ctx) {
      const body = ctx.parsed.body;
      const signals = [
        /^#{2,4}\s+(examples?|usage|sample|samples|walkthroughs?|scenarios?|use cases?)/im,
        /^example\s*\d*\s*:/im,
        /\bfor example[,:]/i,
        /^>\s+/m,
        /\buser says?[:"]/i,
        /^```/m,
      ];
      if (signals.some((re) => re.test(body))) {
        return { status: 'pass', message: 'Concrete examples or samples present.' };
      }
      return {
        status: 'warn',
        message: 'No examples, sample I/O, or quoted scenarios detected.',
        fix: 'Add `## Examples` with at least one walkthrough showing user input and result.',
      };
    },
  },
  {
    id: 'body.has-error-handling',
    category: 'body',
    severity: 'info',
    weight: 2,
    title: 'Has error handling / troubleshooting',
    appliesTo: 'both',
    run(ctx) {
      const body = ctx.parsed.body;
      if (
        /^#{2,4}\s+(troubleshooting|common issues|errors?|error handling|failure modes|gotchas|edge cases)/im.test(body) ||
        /\bif (?:this )?(?:fails|errors|breaks)\b/i.test(body) ||
        /\bon (?:error|failure)\b/i.test(body)
      ) {
        return { status: 'pass', message: 'Error/troubleshooting guidance present.' };
      }
      return {
        status: 'pass',
        message: 'No troubleshooting section (optional but helpful).',
      };
    },
  },
  {
    id: 'body.uses-code-fences',
    category: 'body',
    severity: 'info',
    weight: 1,
    title: 'Uses fenced code blocks',
    appliesTo: 'both',
    run(ctx) {
      const { count } = countCodeBlocks(ctx.parsed.body);
      if (count === 0) {
        return {
          status: 'warn',
          message: 'No fenced code blocks found.',
          fix: 'Use triple-backtick fences for commands and snippets. Improves clarity.',
        };
      }
      return { status: 'pass', message: `${count} fenced block(s).` };
    },
  },
  {
    id: 'body.actionable-language',
    category: 'body',
    severity: 'warn',
    weight: 3,
    title: 'Avoids vague instruction phrases',
    appliesTo: 'both',
    run(ctx) {
      const lines = ctx.parsed.body.split('\n');
      const hits: string[] = [];
      for (const line of lines) {
        for (const p of VAGUE_PHRASES) {
          if (p.test(line) && hits.length < 5) hits.push(line.trim().slice(0, 80));
        }
      }
      if (hits.length > 0) {
        return {
          status: 'warn',
          message: `Vague phrasing found in ${hits.length} line(s).`,
          fix: 'Replace "make sure to…", "as appropriate" with explicit commands and concrete checks.',
        };
      }
      return { status: 'pass', message: 'No vague phrases detected.' };
    },
  },
  {
    id: 'body.size',
    category: 'body',
    severity: 'warn',
    weight: 3,
    title: 'Body size in healthy range',
    appliesTo: 'both',
    run(ctx) {
      const words = wordCount(ctx.parsed.body);
      if (words > 8000) {
        return {
          status: 'fail',
          message: `Body is ${words} words. Far over the 5,000-word guideline.`,
          fix: 'Move details into `references/` and link from SKILL.md.',
        };
      }
      if (words > 5000) {
        return {
          status: 'warn',
          message: `Body is ${words} words. Over the 5,000-word guideline.`,
          fix: 'Trim or offload detail into `references/`.',
        };
      }
      if (words < 60) {
        return {
          status: 'warn',
          message: 'Body is very short. Likely missing instructions or examples.',
        };
      }
      return { status: 'pass', message: `${words} words.` };
    },
  },
  {
    id: 'body.heading-hierarchy',
    category: 'body',
    severity: 'info',
    weight: 1,
    title: 'Heading hierarchy is sensible',
    appliesTo: 'both',
    run(ctx) {
      if (ctx.arch.hasHeadingHierarchyIssue) {
        return {
          status: 'warn',
          message: 'Heading levels skip (e.g. H1 → H3 with no H2).',
          fix: 'Keep heading depth incremental for screen-readers and Claude\'s parsing.',
        };
      }
      return { status: 'pass', message: 'Heading hierarchy is clean.' };
    },
  },
  {
    id: 'body.decision-logic',
    category: 'body',
    severity: 'info',
    weight: 2,
    title: 'Encodes decision logic (if/when/else)',
    appliesTo: 'both',
    run(ctx) {
      const n = ctx.arch.decisionMarkers;
      if (n >= 3) return { status: 'pass', message: `${n} decision markers (rich logic).` };
      if (n >= 1) return { status: 'pass', message: `${n} decision marker(s).` };
      if (ctx.bodyText.wordCount < 200) return { status: 'skipped', message: 'Body too short.' };
      return {
        status: 'warn',
        message: 'No conditional branching ("if X then Y") detected.',
        fix: 'Add decision logic for the common edge cases the skill must handle.',
      };
    },
  },
  {
    id: 'body.validation-gates',
    category: 'body',
    severity: 'info',
    weight: 2,
    title: 'Includes validation / verification steps',
    appliesTo: 'both',
    run(ctx) {
      const n = ctx.arch.validationMarkers;
      if (n >= 3) return { status: 'pass', message: `${n} validation cues.` };
      if (n >= 1) return { status: 'pass', message: `${n} validation cue(s).` };
      if (ctx.bodyText.wordCount < 200) return { status: 'skipped', message: 'Body too short.' };
      return {
        status: 'warn',
        message: 'No "validate / verify / check / ensure" steps detected.',
        fix: 'Add explicit validation steps. The guide stresses these in Pattern 3.',
      };
    },
  },
  {
    id: 'body.example-richness',
    category: 'body',
    severity: 'info',
    weight: 2,
    title: 'Concrete examples present',
    appliesTo: 'both',
    run(ctx) {
      const n = ctx.arch.exampleCount;
      if (n >= 3) return { status: 'pass', message: `${n} example cues.` };
      if (n >= 1) return { status: 'pass', message: `${n} example cue(s).` };
      return {
        status: 'warn',
        message: 'No example phrasing detected.',
        fix: 'Add at least one walkthrough: user says X, actions, result.',
      };
    },
  },
  {
    id: 'body.code-vs-prose',
    category: 'body',
    severity: 'info',
    weight: 2,
    title: 'Code-to-prose ratio is healthy',
    appliesTo: 'both',
    run(ctx) {
      const r = ctx.arch.codeToProseRatio;
      if (ctx.arch.totalCodeLines === 0 && ctx.bodyText.wordCount > 200) {
        return { status: 'pass', message: 'No inline code (may be fine).' };
      }
      if (r > 1.2) {
        return {
          status: 'warn',
          message: `Code lines exceed prose lines (${r.toFixed(2)}x).`,
          fix: 'Move long code into `scripts/` and reference it. SKILL.md should be guidance, not source.',
        };
      }
      return { status: 'pass', message: `Ratio ${r.toFixed(2)} (balanced).` };
    },
  },
  {
    id: 'body.guardrails-when-broad',
    category: 'body',
    severity: 'info',
    weight: 2,
    title: 'Includes guardrails for broad skills',
    appliesTo: 'both',
    run(ctx) {
      const isBroad = ctx.descText.wordCount > 0 && ctx.triggers.distinctTriggerCount < 3;
      if (!isBroad) return { status: 'pass', message: 'Guardrails not required.' };
      if (ctx.arch.hasCriticalSection || ctx.arch.hasGuardrails) {
        return { status: 'pass', message: 'Guardrail language present.' };
      }
      return {
        status: 'warn',
        message: 'Broad skill with no guardrail language ("never", "do not", "must not").',
        fix: 'Add a `## Critical` or `## Guardrails` section listing things Claude must not do.',
      };
    },
  },
  // ---------------------------------------------------------------------------
  // Rules below close gaps surfaced by reviewing Anthropic's
  // "The Complete Guide to Building Skills for Claude" PDF against the existing
  // rule set. Each carries the precise guideRef the gap came from.
  // ---------------------------------------------------------------------------
  {
    id: 'body.mcp-error-handling',
    category: 'body',
    severity: 'warn',
    weight: 3,
    title: 'MCP skills handle tool failures',
    appliesTo: 'both',
    guideRef: 'Patterns and troubleshooting → MCP connection issues',
    run(ctx) {
      const desc = (ctx.parsed.frontmatter.description as string | undefined) ?? '';
      const body = ctx.parsed.body;
      const usesMcp = /\bmcp\b|tool\s+call|tool[-_\s]*use|call\s+(?:the\s+)?[\w-]+\s+tool/i.test(
        `${desc}\n${body}`,
      );
      if (!usesMcp) {
        return { status: 'pass', message: 'Skill does not reference MCP / tool calls.' };
      }
      const handlesFailures = /\b(?:if\s+(?:the\s+)?(?:tool|call|connector|mcp)\s+(?:fails|errors|times?\s*out|is\s+unavailable|returns\s+(?:an\s+)?error)|connection\s+(?:fails|failed|error)|retry|fallback|on\s+(?:tool|mcp|connector|api)\s+(?:error|failure))/i.test(
        body,
      );
      if (handlesFailures) {
        return { status: 'pass', message: 'Body covers MCP failure / retry behaviour.' };
      }
      return {
        status: 'warn',
        message: 'Skill invokes MCP tools but body has no failure / retry guidance.',
        fix: 'Document what Claude should do if the MCP tool returns an error, times out, or is not connected. Include retry / fallback wording.',
      };
    },
  },
  {
    id: 'body.example-format-complete',
    category: 'body',
    severity: 'info',
    weight: 2,
    title: 'Examples follow user-says / actions / result format',
    appliesTo: 'both',
    guideRef: 'Planning and design → Examples section',
    run(ctx) {
      // Only meaningful if the skill actually claims to include examples.
      if (!ctx.arch.exampleCount && !/^#{1,6}\s*examples?\b/im.test(ctx.parsed.body)) {
        return { status: 'pass', message: 'No examples claimed; format check skipped.' };
      }
      const body = ctx.parsed.body;
      const userSays = /\b(?:user\s+says?|user\s+asks?|user\s+requests?|user\s+input|input\s*:|prompt\s*:|user\s*:)/i.test(body);
      const actions = /\b(?:actions?|steps?|claude\s+(?:does|should|will|runs?)|response\s*:|output\s*:|result\s*:|then\s+claude)\b/i.test(body);
      const result = /\b(?:result|output|response|expected|returns?|produces?)\b/i.test(body);
      const score = (userSays ? 1 : 0) + (actions ? 1 : 0) + (result ? 1 : 0);
      if (score >= 2) {
        return { status: 'pass', message: 'Examples include user input + action + result framing.' };
      }
      return {
        status: 'warn',
        message: 'Examples are present but lack the user-says / actions / result framing the guide recommends.',
        fix: 'Format each example as: a user phrasing → the steps Claude takes → the expected result. The guide calls this the three-part example format.',
      };
    },
  },
  {
    id: 'body.sequential-step-ordering',
    category: 'body',
    severity: 'info',
    weight: 2,
    title: 'Sequential workflows show explicit step ordering',
    appliesTo: 'both',
    guideRef: 'Patterns and troubleshooting → Pattern 1: Sequential workflow orchestration',
    run(ctx) {
      const body = ctx.parsed.body;
      // Only score this for workflow-style skills: those that contain at least
      // 4 instruction bullets or a heading mentioning workflow / pipeline /
      // process / orchestration.
      const looksSequential = /\b(workflow|pipeline|process|orchestrat|sequence|multi[-\s]?step|chain)\b/i.test(body)
        || ctx.arch.bulletItems >= 4
        || ctx.arch.numberedItems >= 3;
      if (!looksSequential) {
        return { status: 'pass', message: 'Not a sequential workflow skill.' };
      }
      const explicitSteps = (body.match(/^\s*(?:step\s*\d+|\d+\s*[.)])\s*[A-Z]/gim) || []).length;
      const dependencyCues = /\b(?:after\s+(?:that|step|the\s+previous)|once\s+(?:complete|done|finished)|wait\s+for|before\s+(?:moving|proceeding|the\s+next)|prerequisite)\b/i.test(body);
      if (explicitSteps >= 3 || (explicitSteps >= 1 && dependencyCues)) {
        return { status: 'pass', message: `Explicit step ordering present (${explicitSteps} numbered step(s)).` };
      }
      return {
        status: 'warn',
        message: 'Sequential workflow detected but step ordering / dependencies are implicit.',
        fix: 'Number each step ("### Step 1: ...", "### Step 2: ...") and use dependency phrases like "After step 1 completes" or "Wait for X before Y".',
      };
    },
  },
  {
    id: 'body.multi-mcp-phases',
    category: 'body',
    severity: 'info',
    weight: 2,
    title: 'Multi-tool workflows separate phases and validate between them',
    appliesTo: 'both',
    guideRef: 'Patterns and troubleshooting → Pattern 2: Multi-MCP coordination',
    run(ctx) {
      const declared = (ctx.skillMetadata?.mcpTools ?? (ctx.parsed.frontmatter.mcpTools as string[] | undefined)) || [];
      const body = ctx.parsed.body;
      const toolMentions = (body.match(/\b(?:notion|github|slack|figma|jira|linear|airtable|stripe|hubspot)\b/gi) || []).length;
      const declaredCount = Array.isArray(declared) ? declared.length : 0;
      const isMultiTool = declaredCount >= 2 || toolMentions >= 2;
      if (!isMultiTool) {
        return { status: 'pass', message: 'Not a multi-tool skill.' };
      }
      const hasPhases = /(?:^|\n)#{1,6}\s*(?:phase|stage|part)\s*\d+|phase\s+(?:one|two|1|2)/im.test(body);
      const validationBetween = ctx.arch.validationMarkers >= 2;
      if (hasPhases && validationBetween) {
        return { status: 'pass', message: 'Phases and inter-phase validation are explicit.' };
      }
      if (hasPhases || validationBetween) {
        return {
          status: 'warn',
          message: 'Multi-tool skill has phases OR validation but not both.',
          fix: 'For each phase, add an explicit ## Phase N heading AND a validation gate before the next phase ("verify the output before continuing to Phase 2").',
        };
      }
      return {
        status: 'warn',
        message: 'Multi-tool skill with no phase separation or inter-phase validation.',
        fix: 'Break the workflow into ## Phase 1 / ## Phase 2 / … sections with a validate-before-continue gate between each.',
      };
    },
  },
];
